import getCustomDate from '../utils/getCustomDate';
import MiniCard from './miniCard';
import { monthNames } from '../utils/settings';
import { dateToScrollPosition } from '../utils/datePositionUtils';

// Constants for marker heights and vertical stacking gaps
const SHORT_MARKER_HEIGHT = 10;
const MEDIUM_MARKER_HEIGHT = 15;
const LONG_MARKER_HEIGHT = 25;
const ORDER_LEVEL_GAP = 22;
const STACKED_GAP = 0;
let STARTING_GAP = 15 






STARTING_GAP = STARTING_GAP + LONG_MARKER_HEIGHT; // LONG_MARKER_HEIGHT kısmı alttaki çizgi ve yazıdan kurtulmak için gerekli olan miktar

// Helper function to create common marker styles
const createCommonStyles = (markerType, position) => {
  let height = SHORT_MARKER_HEIGHT;
  if (markerType === 'medium') height = MEDIUM_MARKER_HEIGHT;
  if (markerType === 'long') height = LONG_MARKER_HEIGHT;

  return {
    markerStyle: {
      position: 'absolute',
      left: `${position}px`,
      bottom: '0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      zIndex: 30,
      isolation: 'isolate',
      width: markerType === 'long' ? '1px' : '0.5px',
      height: `${height}px`,
      backgroundColor: markerType === 'long' ? '#333' : '#999',
    },
    labelStyle: {
      position: 'absolute',
      bottom: `${height + 1}px`,
      transform: 'translateX(-50%)',
      fontSize: markerType === 'long' ? '9px' : markerType === 'medium' ? '7.5px' : '7px',
      fontWeight: markerType === 'long' ? '600' : '400',
      color: markerType === 'long' ? '#555' : '#777',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      width: 'max-content',
      letterSpacing: '-0.3px',
      lineHeight: '1',
    }
  };
};

// Updated vertical positioning function – note that we now also update the original event object with altitude
const calculateVerticalPositions = (eventsToRender, orderLevelGap, stackedGap, startingGap) => {
  if (eventsToRender.length == 1){
      eventsToRender[0].altitude = startingGap;
      eventsToRender[0].event.altitude = startingGap;
      return eventsToRender;
  }
  const sortedEvents = [...eventsToRender].sort((a, b) => a.event.startDate - b.event.startDate);
  const groups = {};

  sortedEvents.forEach(eventData => {
    const level = eventData.order_level;
    if (!groups[level]) groups[level] = [];
    groups[level].push(eventData);
  });

  const levels = Object.keys(groups).sort((a, b) => a - b);
  let previousLevelMaxAltitude = 0;
  
  levels.forEach(levelStr => {
    const level = parseInt(levelStr, 10);
    const currentLevelEvents = groups[levelStr];
    let currentBaseAltitude = level === 1 ? startingGap : previousLevelMaxAltitude + orderLevelGap;
    const placedEvents = [];

    currentLevelEvents.forEach(eventData => {
      let finalAltitude = currentBaseAltitude;
      let collidedHeight = 0;

      for (let i = placedEvents.length - 1; i >= 0; i--) {
        const placedEvent = placedEvents[i];
        const eventLeft = eventData.left;
        const eventRight = eventData.left + eventData.width;
        const placedLeft = placedEvent.left;
        const placedRight = placedEvent.left + placedEvent.width;

        const overlap = Math.min(eventRight, placedRight) - Math.max(eventLeft, placedLeft);
        // normalde altta commentli olanı kullanıyoruz vertical positioning için ama adam üst üste geçsin dediği için comment out yaptık
        // if (overlap > 0) {
        if (overlap > 9999999) {
          const eventOverlapRatio = overlap / eventData.width;
          const placedOverlapRatio = overlap / placedEvent.width;
          
          if (eventOverlapRatio >= 0.2 || placedOverlapRatio >= 0.2) {
            collidedHeight = placedEvent.altitude + placedEvent.height + stackedGap;
            break;
          }
        }
      }

      if (collidedHeight > finalAltitude) finalAltitude = collidedHeight;
      
      // Ensure minimum altitude is respected
      finalAltitude = Math.max(finalAltitude, startingGap);
      eventData.altitude = finalAltitude;
      // Also update the original event object so that duration lines can access it
      eventData.event.altitude = finalAltitude;
      placedEvents.push(eventData);
      const eventMaxHeight = finalAltitude + eventData.height;
      if (eventMaxHeight > previousLevelMaxAltitude) {
        previousLevelMaxAltitude = eventMaxHeight;
      }
    });
  });

  return sortedEvents;
};

const renderIntervalTimeline = (
  scrollPosition, 
  interval, 
  visibleWidth, 
  events = [],
  categories,
  stylings,
  scrollPositionY
) => {
  const centerPoint = visibleWidth / 2;
  const intervalValue = interval.value || 'day';
  const pixelWidth = (interval.value === 'day' || interval.value === '6hours') 
    ? (interval.pixelWidth || 30)
    : (interval.pixelWidth || 100);
  
  const centerDate = getCustomDate(scrollPosition, interval);
  const intervalsToShow = Math.ceil(visibleWidth / pixelWidth) + (
    (intervalValue === 'day' || intervalValue === '6hours') ? 40 : 15
  );
  
  const startIdx = Math.floor(intervalsToShow / 2) * -1;
  const endIdx = Math.ceil(intervalsToShow / 2);
  const elements = [];
  
  const computedPositions = new Map();

  for (let i = startIdx; i <= endIdx; i++) {
    let markerDate = centerDate.clone().addIntervals(i, intervalValue);
    let nextMarkerDateForEvents = centerDate.clone().addIntervals(i + 1, intervalValue);
    
    // Yıl bazlı intervallerde (2year, 5year vb) markerDate'i tam yıla yuvarlayalım
    // Böylece 2024, 2026, 2029 gibi kaymalar olmaz, 2024, 2026, 2028 gider (2 yıllık ise)
    if (['2years', '5years', '20years', '50years'].includes(intervalValue)) {
       // Yuvarlama mantığı eklenebilir ama şu anlık dateToScrollPosition doğru çalışıyorsa gerek yok.
       // Sadece görsel olarak "long" marker'ın hangi yıla denk geldiğini kontrol ederken modülo kullanacağız.
    }

    const position = centerPoint + (i * pixelWidth);


    let markerType = 'short';
    const year = markerDate.getFullYear();
    const month = markerDate.getMonth();
    const day = markerDate.getDate();
    const hour = markerDate.getHours();
    const isFirstDayOfYear = month === 0 && day === 1;
    const isFirstDayOfMonth = day === 1;
    const isFirstOfDay = hour === 0;
    
    switch (intervalValue) {
      case '6hours':
        if (isFirstDayOfYear) markerType = 'long';
        else if (isFirstDayOfMonth || isFirstOfDay) markerType = 'medium';
        break;
      case 'day':
        if (isFirstDayOfYear) markerType = 'long';
        else if (isFirstDayOfMonth) markerType = 'medium';
        break;
      case '3months':
        if (isFirstDayOfYear) markerType = 'long';
        else if (isFirstDayOfMonth && month % 3 === 0) markerType = 'medium';
        break;
      case 'month':
        if (isFirstDayOfYear) markerType = 'long';
        else if (isFirstDayOfMonth) markerType = 'medium';
        break;
      case '2weeks':
      case 'week':
        if (isFirstDayOfYear) markerType = 'long';
        else if (isFirstDayOfMonth) markerType = 'medium';
        break;
      
      case '2years':
        // 2 yılda bir Long marker olsun, ama hangisi? Çift yıllar mesela.
        if (isFirstDayOfYear && year % 2 === 0) markerType = 'long';
        else if (isFirstDayOfYear) markerType = 'medium'; 
        break;

      case '5years':
        // Tüm ara çizgiler uzun olsun (Kullanıcı kalın çizgi istedi)
        markerType = 'long';
        break;

      case '10years':
        markerType = 'long';
        break;

      case '20years':
        markerType = 'long';
        break;

      case '50years':
        markerType = 'long';
        break;
      case 'year':
      case '100years':
      default:
        if (isFirstDayOfYear) markerType = 'long';
        break;
    }
    
    let dateLabel = '';
    if (markerType === 'long') {
      const displayYear = markerDate.getFullYear();
      dateLabel = displayYear < 0 ? `MÖ ${Math.abs(displayYear)}` : `${displayYear}`;
    } else if (markerType === 'medium') {
      if (['year', '2years', '5years', '10years', '20years', '50years', '100years'].includes(intervalValue) && isFirstDayOfYear) {
         const displayYear = markerDate.getFullYear();
         dateLabel = displayYear < 0 ? `MÖ ${Math.abs(displayYear)}` : `${displayYear}`;
      } else if (intervalValue === '6hours' && isFirstOfDay && !isFirstDayOfMonth) {
        dateLabel = `${day} ${monthNames[month]}`;
      } else {
        dateLabel = monthNames[month];
      }
    } else {
      switch (intervalValue) {
        case '6hours':
          dateLabel = `${hour}:00`;
          break;
        case 'day':
        case 'week':
        case '2weeks':
          dateLabel = `${day}`;
          break;
        case '3months':
        case 'year':
        case '2years':
        case '5years':
        case '20years':
        case '50years':
        case '10years':
        case '100years':
          dateLabel = (day === 1) ? monthNames[month] : `${day}`;
          break;
        default: break;
      }
    }
    
    if ((intervalValue === 'week' || intervalValue === '2weeks')) {
      const nextPotentialMarkerDate = centerDate.clone().addIntervals(i + 1, intervalValue);

      if (nextPotentialMarkerDate.getMonth() !== markerDate.getMonth() || nextPotentialMarkerDate.getFullYear() !== markerDate.getFullYear()) {
        const firstOfNextPeriod = markerDate.clone();
        firstOfNextPeriod.setDate(1);
        firstOfNextPeriod.setMonth(firstOfNextPeriod.getMonth() + 1);
        firstOfNextPeriod.setHours(0, 0, 0, 0);

        if (firstOfNextPeriod.getTime() > markerDate.getTime() && firstOfNextPeriod.getTime() <= nextPotentialMarkerDate.getTime()) {
          const isFirstOfNewYear = firstOfNextPeriod.getMonth() === 0 && firstOfNextPeriod.getDate() === 1;

          markerType = 'long';

          if (isFirstOfNewYear) {
            const displayYear = firstOfNextPeriod.getFullYear();
            dateLabel = displayYear < 0 ? `MÖ ${Math.abs(displayYear)}` : `${displayYear}`;
          } else {
            dateLabel = monthNames[firstOfNextPeriod.getMonth()];
          }
        }
      }
    }
    
    const styles = createCommonStyles(markerType, position);
    elements.push(
      <div key={`marker-${interval.value}-${i}-${markerDate.getTime()}`} style={styles.markerStyle}>
        {dateLabel && <div style={styles.labelStyle}>{dateLabel}</div>}
      </div>
    );
    
    const markerEvents = events.filter(event => {
      // Skip events that should not be rendered
      if (event.shouldRender === false) return false;
      // Filter by date range
      const eventTime = event.startDate.getTime();
      return eventTime >= markerDate.getTime() && eventTime < nextMarkerDateForEvents.getTime();
    });
    
    markerEvents.forEach((event) => {
      const eventTime = event.startDate.getTime();
      const event_datetime_diff = eventTime - markerDate.getTime();
      const markers_datetime_diff = nextMarkerDateForEvents.getTime() - markerDate.getTime();
      const event_horizontal_pixel_shift = markers_datetime_diff !== 0
          ? (event_datetime_diff * pixelWidth) / markers_datetime_diff
          : 0;

      const leftPosition = position + event_horizontal_pixel_shift;

      computedPositions.set(event._id, {
        event,
        left: leftPosition,
        width: event.width,
        height: event.height,
        order_level: event.order_level || 1,
        shouldRender: event.shouldRender
      });
    });
  }
  
  events.forEach(event => {
    if (!computedPositions.has(event._id)) {
      // Add null check for startDate before calling dateToScrollPosition
      const leftPosition = event.startDate 
        ? dateToScrollPosition(event.startDate, interval) - scrollPosition + centerPoint
        : centerPoint; // Default to center if no startDate


      computedPositions.set(event._id, {
        event,
        left: leftPosition,
        width: event.width,
        height: event.height,
        order_level: event.order_level || 1,
        shouldRender: event.shouldRender
      });
    }
  });
  
  // Create a map to track altitude values for all event IDs
  const eventAltitudes = new Map();
  
  // Filter events to only include those that should be rendered
  const allEvents = Array.from(computedPositions.values()).filter(eventData => 
    eventData.event.shouldRender !== false
  );
  
  // console.log(`Filter diagnostics: 
  //   Total events available: ${computedPositions.size}
  //   Events to render: ${allEvents.length}
  // `);
  
  // Use filtered events for positioning
  const eventsToRender = allEvents;
  
  // console.log(`Rendering ${eventsToRender.length} events after filtering`);
  
  const positionedEvents = calculateVerticalPositions(
    eventsToRender,
    ORDER_LEVEL_GAP,
    STACKED_GAP,
    STARTING_GAP
  );

  // Copy altitude values from positioned events to all events for line calculations
  positionedEvents.forEach(eventData => {
    eventAltitudes.set(eventData.event._id, eventData.altitude);
  });

  events.forEach(event => {
      event.altitude = eventAltitudes.get(event._id);
  });

  
  positionedEvents.forEach(eventData => {
    // Use a minimum bottom position to avoid overlapping with the edge lines
    // Make the minimum value match the STARTING_GAP for consistency
    const minimumBottom = STARTING_GAP + scrollPositionY; 
    const calculatedBottom = eventData.altitude + scrollPositionY;
    const finalBottom = Math.max(calculatedBottom, minimumBottom);
    
    elements.push(
      <div 
        key={`event-${eventData.event._id}`}
        style={{
          position: 'absolute',
          left: `${eventData.left}px`,
          bottom: `${finalBottom}px`,
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 20,
        }}
      >
        <MiniCard
          event={eventData.event}
          categories={categories}
          stylings={stylings}
          onClick={() => {
            document.dispatchEvent(new CustomEvent('selectTimelineEvent', { detail: eventData.event }));
            if (typeof window.selectEvent === 'function') {
              window.selectEvent(eventData.event);
            }
          }}
          zIndex={20}
        />
      </div>
    );
  });

  return elements;
};

function getVisibleMarkersInfo(scrollPosition, interval, visibleWidth) {
  const centerPoint = visibleWidth / 2;
  const intervalValue = interval.value || 'day';
  const pixelWidth = (interval.value === 'day' || interval.value === '6hours') 
    ? (interval.pixelWidth || 30)
    : (interval.pixelWidth || 100);
  
  const centerDate = getCustomDate(scrollPosition, interval);
  const visibleIntervals = Math.ceil(visibleWidth / pixelWidth) + 2;
  const startIdx = Math.floor(visibleIntervals / 2) * -1;
  const endIdx = Math.ceil(visibleIntervals / 2);
  
  let count = 0;
  let leftmostDate = null;
  let rightmostDate = null;
  
  for (let i = startIdx; i <= endIdx; i++) {
    const position = centerPoint + (i * pixelWidth);
    const markerDate = centerDate.clone().addIntervals(i, intervalValue);
    
    if (position >= 0 && position <= visibleWidth) {
      count++;
      if (!leftmostDate) leftmostDate = markerDate.clone();
      rightmostDate = markerDate.clone();
    }
  }
  
  return { count, leftmostDate, rightmostDate };
}

export { renderIntervalTimeline, getVisibleMarkersInfo, STARTING_GAP, LONG_MARKER_HEIGHT };
